export enum OcTreeHierarchyLevel {
    Parent = 'parent',
    Sibling = 'sibling'
}

export enum OcTreeDragPosition {
    Before = 'before',
    After = 'after',
    In = 'in'
}

export interface OcTreeItemBasicProps<T> {
    id: string
    isNew: boolean
    isDirty: boolean
    parentId?: string
    isValid: boolean
    children: T[]
    hasParentChanged?: boolean
    highlightChange?: boolean
}

export interface OcTreeItem<T extends OcTreeItemBasicProps<T>> {
    data: T
    children: OcTreeItem<T>[]
    expanded: boolean
    visibleInGrid?: boolean
    gridRowId: number
    defaultSearchField?: string
}

/**
 * Walks down the item children in prefix order.
 * @param start Item from which to start
 * @param eqAction Action executed on each item in subtree. Return true/false to say if you want to continue with children.
 * @param sortFunction Sort function for children, can be skipped in case order does not matter
 * @param skipFirst Skip the start item from the walk
 */
export function ocTreeItemWalkDown<T extends OcTreeItemBasicProps<T>>(
    start: OcTreeItem<T> | T,
    eqAction: (e: OcTreeItem<T> | T) => boolean,
    sortFunction?: (a: T, b: T, parent: T) => number,
    skipFirst = false
): void {
    let result = false
    if (start && !skipFirst) {
        result = eqAction(start)
    }

    if (start && (result || skipFirst)) {
        const workingItems = sortFunction
            ? start?.children.sort((a, b) =>
                  sortFunction((a as any).data || a, (b as any).data || b, (start as any).data || start)
              )
            : start?.children
        workingItems?.forEach(c => ocTreeItemWalkDown(c, eqAction, sortFunction, false))
    }
}

/**
 * Walks up the item parents.
 * @param start Item from which to start
 * @param eqAction Action executed on each parent. Return true/false to say if you want to continue with parent.
 * @param skipFirst Skip the start item from the walk
 */
export function ocTreeItemWalkUp<T extends OcTreeItemBasicProps<T>>(
    itemList: OcTreeItem<T>[] | T[],
    start: OcTreeItem<T> | T,
    eqAction: (e: OcTreeItem<T> | T) => boolean,
    skipFirst = false
): void {
    let runIdx = 0
    let current: OcTreeItem<T> | T | undefined = start
    const findItem = (item: OcTreeItem<T>) => item.data.id === (current as OcTreeItem<T>)?.data.parentId
    const findItem2 = (item: T) => item.id === (current as T)?.parentId

    while (current) {
        if (!(runIdx === 0 && skipFirst)) {
            if (!eqAction(current)) {
                break
            }
        }
        if ('data' in current) {
            current = (itemList as OcTreeItem<T>[]).find(findItem)
        } else {
            current = (itemList as T[]).find(findItem2)
        }

        runIdx++
    }
}
